import {Injectable} from '@angular/core';
import {from, Observable, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {plainToClass} from 'class-transformer';
import {ChatAttachmentApiModel} from '../models/chat-attachment.api-model';
import {UploadPreprocessService} from '../../core/services/upload-preprocess.service';
import {UploadPreprocessCategory} from '../../core/interfaces/prepared-upload.interface';

@Injectable({
  providedIn: 'root'
})
export class ChatAttachmentRestService {
  private readonly resource: string = '/chat_attachments';

  constructor(
    private http: HttpClient,
    private readonly uploadPreprocess: UploadPreprocessService,
  ) {
  }

  upload(file: FormData): Observable<ChatAttachmentApiModel> {
    return this.http.post<ChatAttachmentApiModel>(environment.apiUrl + this.resource, file)
      .pipe(map((response: ChatAttachmentApiModel) => plainToClass(ChatAttachmentApiModel, response)));
  }

  uploadAttachment(file: File, type: string): Observable<ChatAttachmentApiModel> {
    const category = this.resolvePreprocessCategory(type, file);
    return from(this.uploadPreprocess.prepare(file, category, 'chat_attachments.upload')).pipe(
      switchMap((preparedUpload) => {
        const fd = new FormData();
        fd.append('file', preparedUpload.file, preparedUpload.file.name);
        fd.append('type', type);
        return this.upload(fd).pipe(
          tap(() => this.uploadPreprocess.markUploaded(preparedUpload)),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  private resolvePreprocessCategory(type: string, file: File): UploadPreprocessCategory {
    if (type === 'IMAGE' || file?.type?.startsWith('image/')) {
      return 'image';
    }

    if (type === 'VIDEO' || file?.type?.startsWith('video/')) {
      return 'video';
    }

    return 'document';
  }
}
